import type { HealthResponse } from '@scc/shared';

export interface MetaResponse {
  version: string;
  environment: string;
  github: { mode: 'pat' | 'app' | 'none'; configured: boolean; reason?: string };
}

export interface AuthConfig {
  oauthConfigured: boolean;
  bootstrapAvailable: boolean;
  allowlistSize: number;
  /** Si se puede pedir un código al correo. Falso sin SMTP: la opción no se ofrece. */
  emailLoginAvailable: boolean;
}

export interface Me {
  login: string;
  name: string;
  role: 'admin' | 'member' | 'observer';
  avatarUrl: string | null;
  /** Techo de subida en MB para imágenes, tal como lo tiene el servidor. No se adivina. */
  attachmentsMaxMb: number;
  /** El mismo techo para vídeo. Es más alto: un clip nunca pesa lo que una captura. */
  attachmentsVideoMaxMb: number;
  /** Y para documentos (PDF, Office, texto). */
  attachmentsDocMaxMb: number;
}

export interface Kpi {
  key: string;
  label: string;
  value: number | null;
  unavailableReason?: string;
  detail?: string;
  tone?: 'neutral' | 'good' | 'warn' | 'bad';
  href?: string;
}

export interface ProjectRow {
  slug: string;
  name: string;
  status: string;
  /** Procedencia del estado: hoy siempre 'manual'. Ver docs/21. */
  statusSource: string;
  statusSetBy: string | null;
  statusAgeDays: number | null;
  priority: string;
  progressPct: number | null;
  repoNames: string | null;
  defaultBranch: string | null;
  hasCi: boolean;
  prsOpen: number;
  prsOpenHuman: number;
  prsOpenBot: number;
  prsStale: number;
  runsFailed7d: number;
  /** Fallos en la rama que corre en producción (7 d). Enciende el rojo. */
  runsFailedProd7d: number;
  /** Fallos en ramas que todavía existen (7 d). Un fallo en una rama borrada no cuenta. */
  runsFailedLive7d: number;
  /** Verdes seguidas en la rama de producción desde el último fallo. */
  prodGreenStreak: number;
  runFailurePct: number | null;
  runFailurePct30d: number | null;
  runs30d: number;
  commits30d: number;
  tagsProduction: number;
  lastActivityAt: string | null;
  productionUrl: string | null;
  unmanagedRepoCount: number;
  syncFailures: number;
  tasksOpen: number | null;
  nextMilestoneName: string | null;
  health: 'ok' | 'warn' | 'bad';
  healthReasons: string[];
}

export interface BranchInfo {
  name: string;
  repo: string;
  shortSha: string;
  isDefault: boolean;
  isProtected: boolean;
  aheadBy: number | null;
  behindBy: number | null;
  headCommitAt: string | null;
  idleDays: number | null;
  hasOpenPr: boolean;
  flags: string[];
}

export interface ProjectDetail {
  id: string;
  metrics: ProjectRow;
  description: string | null;
  notes: string | null;
  startDate: string | null;
  targetDate: string | null;
  docsUrls: string[];
  progressSource: string;
  ownerName: string | null;
  ownerLogin: string | null;
  techDetectedAt: string | null;
  repos: Array<Record<string, unknown>>;
  technologies: Array<Record<string, unknown>>;
  unmanagedCodebases: Array<Record<string, unknown>>;
  environments: Array<Record<string, unknown>>;
  branches: BranchInfo[];
  commits: Array<Record<string, unknown>>;
  pulls: Array<Record<string, unknown>>;
  runs: Array<Record<string, unknown>>;
  tags: Array<Record<string, unknown>>;
  tagStats: { byLifecycle: Record<string, number>; byKind: Record<string, number>; shown: number };
  releases: Array<Record<string, unknown>>;
  deployments: Array<Record<string, unknown>>;
  activity: Array<Record<string, unknown>>;
  syncState: Array<Record<string, unknown>>;
  pending: { roadmap: string; tasks: string; risks: string };
}

export interface AttentionItem {
  severity: 'critical' | 'high' | 'medium';
  kind: string;
  title: string;
  detail: string;
  project?: string;
  href?: string;
}

export interface DashboardResponse {
  kpis: Kpi[];
  projects: ProjectRow[];
  attention: AttentionItem[];
  generatedAt: string;
}

export interface ActivityEntry {
  kind: string;
  at: string;
  project_slug: string;
  repo: string;
  title: string;
  actor: string | null;
  url: string | null;
  detail: string | null;
}

export interface PullRequestEntry {
  number: number;
  title: string;
  repo: string;
  author: string | null;
  project_slug: string | null;
  project_name: string | null;
  head_ref: string | null;
  base_ref: string | null;
  is_draft: boolean;
  is_bot_author: boolean;
  merged: boolean;
  review_decision: string | null;
  review_count: number;
  additions: number | null;
  deletions: number | null;
  changed_files: number | null;
  created_at: string | null;
  merged_at: string | null;
  html_url: string | null;
  ageDays: number | null;
}

export interface CiResponse {
  repos: Array<{
    repo: string;
    defaultBranch: string;
    projectSlug: string | null;
    projectName: string | null;
    workflows: number;
    runs: number;
    failed: number;
    failed7d: number;
    runs30d: number;
    failed30d: number;
    /** Fallos de 7 d en ramas que todavía existen: los que se pueden arreglar. */
    failedLive7d: number;
    failurePct: number | null;
    /** Tasa reciente: la que decide el color. `failurePct` queda como histórico. */
    failurePct30d: number | null;
    lastRunAt: string | null;
    hasCi: boolean;
  }>;
  recentFailures: Array<{
    id: number;
    name: string | null;
    branch: string | null;
    isDefaultBranch: boolean;
    event: string | null;
    startedAt: string | null;
    durationMs: number | null;
    url: string | null;
    repo: string;
    projectSlug: string | null;
    actor: string | null;
  }>;
}

export interface WorkItem {
  id: number;
  projectSlug: string;
  projectName: string;
  trackId: number | null;
  trackCode: string | null;
  trackName: string | null;
  kind: 'feature' | 'task' | 'bug';
  title: string;
  description: string | null;
  state: string;
  stateSource: string;
  priority: string;
  assigneeName: string | null;
  assigneeLogin: string | null;
  progressPct: number | null;
  startDate: string | null;
  targetDate: string | null;
  closedAt: string | null;
  environment: string | null;
  branchName: string | null;
  origin: string;
  sourceRef: string | null;
  milestoneName: string | null;
  linkCount: number;
  evidenceCount: number;
  isOverdue: boolean;
  closedWithoutEvidence: boolean;
  updatedAt: string | null;
}

export interface RoadmapResponse {
  tracks: Array<{
    id: number;
    code: string | null;
    name: string;
    description: string | null;
    state: string;
    priority: string;
    progressPct: number | null;
    progressSource: string;
    startDate: string | null;
    targetDate: string | null;
    origin: string;
    sourceRef: string | null;
    ownerName: string | null;
    itemCount: number;
    openCount: number;
  }>;
  items: WorkItem[];
  milestones: Array<{
    id: number;
    name: string;
    description: string | null;
    dueDate: string | null;
    completedAt: string | null;
    origin: string;
  }>;
  proposals: Array<{
    id: number;
    kind: string;
    code: string | null;
    name: string;
    description: string | null;
    proposedState: string | null;
    proposedPriority: string | null;
    sourceRef: string;
    payload: unknown;
    detectedAt: string;
  }>;
}

export interface RisksResponse {
  risks: Array<{
    id: number;
    code: string | null;
    title: string;
    description: string | null;
    probability: string;
    impact: string;
    mitigation: string | null;
    status: string;
    evidence: string | null;
    detectedAt: string;
    closedAt: string | null;
    projectSlug: string | null;
    projectName: string | null;
    ownerName: string | null;
  }>;
  blockers: Array<{
    id: number;
    title: string;
    description: string | null;
    causeKind: string | null;
    openedAt: string;
    resolvedAt: string | null;
    blockedDays: number;
    projectSlug: string | null;
    itemTitle: string | null;
    ownerName: string | null;
  }>;
}

export interface AdminRepositoriesResponse {
  repositories: Array<{
    id: number;
    fullName: string;
    visibility: string;
    defaultBranch: string;
    isArchived: boolean;
    isFork: boolean;
    language: string | null;
    pushedAt: string | null;
    htmlUrl: string | null;
    projectSlug: string | null;
    projectName: string | null;
    commits: number;
    branches: number;
    lastSyncAt: string | null;
  }>;
  unmanagedCodebases: Array<{
    slug: string;
    vpsPath: string;
    hasLocalGit: boolean;
    localCommits: number | null;
    lastCommitAt: string | null;
    servesUrl: string | null;
    reason: string | null;
    resolvedAt: string | null;
    note: string | null;
    projectSlug: string | null;
  }>;
  projects: Array<{ slug: string; name: string }>;
}

export interface AdminPeopleResponse {
  people: Array<{
    id: number;
    displayName: string;
    email: string | null;
    githubLogin: string | null;
    gitEmails: string[];
    role: string;
    isActive: boolean;
    commits: number;
    ownsProjects: number;
  }>;
  identities: Array<{
    email: string;
    nameSample: string | null;
    commits: number;
    repos: number;
    firstCommitAt: string | null;
    lastCommitAt: string | null;
    matched: boolean;
  }>;
}

export interface AdminSyncResponse {
  state: Array<{
    repo: string;
    resource: string;
    lastSuccessAt: string | null;
    lastSyncedAt: string | null;
    items: number | null;
    failures: number;
    skippedReason: string | null;
    lastError: string | null;
    hasEtag: boolean;
  }>;
  runs: Array<{
    id: number;
    target: string | null;
    mode: string;
    startedAt: string | null;
    finishedAt: string | null;
    status: string;
    apiCalls: number | null;
    rateRemaining: number | null;
    error: string | null;
  }>;
  queue: Array<{ name: string; state: string; count: number }>;
  webhooks: Array<{
    deliveryId: string;
    event: string;
    action: string | null;
    status: string;
    attempts: number;
    lastError: string | null;
    receivedAt: string;
    processedAt: string | null;
    repo: string | null;
    latencyMs: number | null;
  }>;
  webhookSummary: { total: number; failed: number; pending: number };
}

export interface AuditEntry {
  id: number;
  actorLogin: string | null;
  action: string;
  entityType: string | null;
  entityId: number | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  at: string;
}

/** Se lanza cuando la API responde 401: la interfaz manda a la pantalla de acceso. */
export class UnauthenticatedError extends Error {
  constructor() {
    super('Sin sesión');
    this.name = 'UnauthenticatedError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    credentials: 'same-origin',
  });

  if (response.status === 401) throw new UnauthenticatedError();

  // El healthcheck responde 503 con un cuerpo válido cuando está degradado:
  // se devuelve el cuerpo para poder mostrar el motivo.
  if (!response.ok && response.status !== 503) {
    let message = `${path} respondió ${response.status}`;
    try {
      const body = (await response.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // Sin cuerpo JSON: se queda el mensaje genérico.
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

export const fetchHealth = (): Promise<HealthResponse> => request<HealthResponse>('/api/health');
export const fetchMeta = (): Promise<MetaResponse> => request<MetaResponse>('/api/meta');
export const fetchAuthConfig = (): Promise<AuthConfig> => request<AuthConfig>('/api/auth/config');
export const fetchMe = (): Promise<Me> => request<Me>('/api/auth/me');
export const fetchDashboard = (): Promise<DashboardResponse> =>
  request<DashboardResponse>('/api/dashboard');
export const fetchProjects = (): Promise<ProjectRow[]> => request<ProjectRow[]>('/api/projects');
export const fetchProject = (slug: string): Promise<ProjectDetail> =>
  request<ProjectDetail>(`/api/projects/${encodeURIComponent(slug)}`);
export const fetchActivity = (limit = 60): Promise<ActivityEntry[]> =>
  request<ActivityEntry[]>(`/api/activity?limit=${limit}`);
export const fetchPullRequests = (state: 'open' | 'merged' = 'open'): Promise<PullRequestEntry[]> =>
  request<PullRequestEntry[]>(`/api/pull-requests?state=${state}`);
export const fetchCi = (): Promise<CiResponse> => request<CiResponse>('/api/ci');

export const loginWithBootstrapToken = (token: string): Promise<Me> =>
  request<Me>('/api/auth/bootstrap', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

/**
 * Pedir un código al correo.
 *
 * Responde igual exista o no la dirección, así que la pantalla NO debe interpretar el éxito
 * como «ese correo tiene acceso». Es a propósito: lo contrario sería un detector de
 * direcciones con acceso al portal.
 */
export const requestEmailCode = (email: string): Promise<{ ok: true }> =>
  request<{ ok: true }>('/api/auth/email/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

/** Entregar el código. Deja la misma sesión que entrar con GitHub. */
export const loginWithEmailCode = (
  email: string,
  code: string,
): Promise<{ login: string; role: string }> =>
  request<{ login: string; role: string }>('/api/auth/email/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });

export const logout = (): Promise<{ ok: true }> =>
  request<{ ok: true }>('/api/auth/logout', { method: 'POST' });

// ─── Administración ─────────────────────────────────────────────────────────

export const fetchAdminRepositories = (): Promise<AdminRepositoriesResponse> =>
  request<AdminRepositoriesResponse>('/api/admin/repositories');
export const fetchAdminPeople = (): Promise<AdminPeopleResponse> =>
  request<AdminPeopleResponse>('/api/admin/people');
export const fetchAdminSync = (): Promise<AdminSyncResponse> =>
  request<AdminSyncResponse>('/api/admin/sync');
export const fetchAdminAudit = (limit = 100): Promise<AuditEntry[]> =>
  request<AuditEntry[]>(`/api/admin/audit?limit=${limit}`);

const write = <T>(path: string, method: string, body: unknown): Promise<T> =>
  request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const assignRepository = (
  repoId: number,
  projectSlug: string | null,
): Promise<{ ok: true }> =>
  write<{ ok: true }>(`/api/admin/repositories/${repoId}/project`, 'PATCH', { projectSlug });

export const updatePerson = (
  personId: number,
  body: { displayName?: string; githubLogin?: string | null; gitEmails?: string[]; role?: string },
): Promise<{ ok: true }> => write<{ ok: true }>(`/api/admin/people/${personId}`, 'PATCH', body);

export const updateProject = (slug: string, body: Record<string, unknown>): Promise<{ ok: true }> =>
  write<{ ok: true }>(`/api/admin/projects/${slug}`, 'PATCH', body);

// ─── Trabajo propio (H6) ────────────────────────────────────────────────────

export const fetchWorkItems = (
  filters: Record<string, string | undefined> = {},
): Promise<WorkItem[]> => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const suffix = query.toString();
  return request<WorkItem[]>(`/api/work-items${suffix ? `?${suffix}` : ''}`);
};

export const fetchRoadmap = (slug: string): Promise<RoadmapResponse> =>
  request<RoadmapResponse>(`/api/projects/${encodeURIComponent(slug)}/roadmap`);

export const fetchRisks = (projectSlug?: string): Promise<RisksResponse> =>
  request<RisksResponse>(
    `/api/risks${projectSlug ? `?project=${encodeURIComponent(projectSlug)}` : ''}`,
  );

export const decideProposal = (
  id: number,
  decision: 'accept' | 'reject',
): Promise<{ ok: true; createdId?: number }> =>
  write<{ ok: true; createdId?: number }>(`/api/proposals/${id}/${decision}`, 'POST', {});

export const createWorkItem = (body: Record<string, unknown>): Promise<{ id: number }> =>
  write<{ id: number }>('/api/work-items', 'POST', body);

export const updateWorkItem = (id: number, body: Record<string, unknown>): Promise<{ ok: true }> =>
  write<{ ok: true }>(`/api/work-items/${id}`, 'PATCH', body);

export const updateRisk = (id: number, body: Record<string, unknown>): Promise<{ ok: true }> =>
  write<{ ok: true }>(`/api/risks/${id}`, 'PATCH', body);

export const requestSync = (body: {
  repo?: string;
  mode?: 'full' | 'incremental';
}): Promise<{ enqueued: number; queue: string }> =>
  write<{ enqueued: number; queue: string }>('/api/admin/sync', 'POST', body);

// ─── Conversación ────────────────────────────────────────────────────────────

export interface Comment {
  id: string;
  entity_type: 'project' | 'work_item';
  entity_id: string;
  author_login: string;
  author_name: string | null;
  kind: 'comment' | 'feature_request';
  body: string;
  work_item_id: string | null;
  work_item_title: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  edited_at: string | null;
}

export const fetchComments = (
  entityType: 'project' | 'work_item',
  entityId: string | number,
): Promise<{ comments: Comment[] }> =>
  request<{ comments: Comment[] }>(
    `/api/comments?entityType=${entityType}&entityId=${encodeURIComponent(String(entityId))}`,
  );

export const createComment = (body: {
  entityType: 'project' | 'work_item';
  entityId: string | number;
  kind: 'comment' | 'feature_request';
  body: string;
}): Promise<{ id: string }> => write('/api/comments', 'POST', body);

export const editComment = (id: string, body: string): Promise<{ ok: true }> =>
  write(`/api/comments/${id}`, 'PATCH', { body });

export const deleteComment = (id: string): Promise<{ ok: true }> =>
  request(`/api/comments/${id}`, { method: 'DELETE' });

export const resolveRequest = (
  id: string,
  body: { workItemId?: string | null; reason?: string },
): Promise<{ ok: true }> => write(`/api/comments/${id}/resolve`, 'POST', body);

// ─── Personas con acceso ─────────────────────────────────────────────────────

export interface AccessPerson {
  id: string;
  display_name: string;
  github_login: string | null;
  auth_role: 'admin' | 'member' | 'observer' | null;
  can_login: boolean;
  is_active: boolean;
  notify_email: boolean;
  email: string | null;
  invited_by: string | null;
  last_login_at: string | null;
  tareas_abiertas: number;
}

export const fetchPeople = (): Promise<{
  people: AccessPerson[];
  roles: { value: string; label: string }[];
}> => request('/api/people');

export const invitePerson = (body: {
  githubLogin: string;
  displayName?: string;
  role: string;
}): Promise<{ id: string }> => write('/api/people', 'POST', body);

export const changeAccess = (
  id: string,
  body: { role?: string; canLogin?: boolean; notifyEmail?: boolean },
): Promise<{ ok: true }> => write(`/api/people/${id}/access`, 'PATCH', body);

// ─── Documentación espejada del repositorio ──────────────────────────────────

export interface ProjectDoc {
  id: string;
  path: string;
  title: string;
  bytes: number;
  category: string | null;
  category_set_by: string | null;
  html_url: string | null;
  synced_at: string;
}

export interface ProjectDocFull extends ProjectDoc {
  content: string;
  project_slug: string;
  project_name: string;
}

export const fetchProjectDocs = (
  slug: string,
  q = '',
): Promise<{
  documents: ProjectDoc[];
  categories: { value: string; label: string }[];
  /** El `MAPA.md` del proyecto, si lo tiene. Viene aparte porque se fija arriba. */
  map: ProjectDoc | null;
}> => request(`/api/projects/${encodeURIComponent(slug)}/documents?q=${encodeURIComponent(q)}`);

/**
 * Qué proyectos tienen mapa y cuáles no.
 *
 * Incluye los que NO lo tienen, con `documentId` en null: el hueco es el dato accionable.
 */
export const fetchDocMaps = (): Promise<{
  projects: { slug: string; name: string; documentId: string | null }[];
}> => request('/api/documents/maps');

export const fetchDocument = (id: string): Promise<ProjectDocFull> =>
  request(`/api/documents/${id}`);

export const setDocumentCategory = (id: string, category: string | null): Promise<{ ok: true }> =>
  write(`/api/documents/${id}/category`, 'PATCH', { category });

export interface ProjectDocGlobal extends ProjectDoc {
  project_slug: string;
  project_name: string;
}

export const fetchAllDocs = (f: {
  project?: string;
  category?: string;
  q?: string;
}): Promise<{
  documents: ProjectDocGlobal[];
  categories: { value: string; label: string }[];
  total: number;
  truncated: boolean;
}> =>
  request(
    `/api/documents?project=${encodeURIComponent(f.project ?? '')}` +
      `&category=${encodeURIComponent(f.category ?? '')}&q=${encodeURIComponent(f.q ?? '')}`,
  );

/** Un adjunto de una tarea. `url` la sirve la API, no nginx: exige sesión. */
export interface Attachment {
  id: string;
  filename: string;
  mimeType: string;
  bytes: number;
  uploadedBy: string | null;
  createdAt: string;
  url: string;
}

export const fetchAttachments = (workItemId: number | string): Promise<Attachment[]> =>
  request<{ attachments: Attachment[] }>(`/api/work-items/${workItemId}/attachments`).then(
    (r) => r.attachments,
  );

/**
 * Sube un adjunto.
 *
 * Sin `Content-Type` a propósito: con `FormData` lo pone el navegador, y tiene que incluir
 * el `boundary` que él mismo genera. Fijarlo a mano rompe la subida de una forma que en el
 * servidor se ve como «no llegó ningún fichero».
 */
export const uploadAttachment = (
  workItemId: number | string,
  file: File,
): Promise<{ ok: true; id: string; url: string }> => {
  const datos = new FormData();
  datos.append('file', file);
  return request<{ ok: true; id: string; url: string }>(
    `/api/work-items/${workItemId}/attachments`,
    { method: 'POST', body: datos },
  );
};

export const deleteAttachment = (id: string): Promise<{ ok: true }> =>
  request<{ ok: true }>(`/api/attachments/${id}`, { method: 'DELETE' });

// ─── Conexiones de Claude (OAuth del MCP, fase 3) ─────────────────────────────

export interface SolicitudOAuth {
  /** Host comprobado de la aplicación que pide acceso (p. ej. claude.ai). */
  cliente: string;
  /** Nombre que la aplicación dice tener. Se muestra en segundo plano: no está comprobado. */
  nombre: string | null;
  /** A dónde vuelve el navegador después. */
  destino: string;
  /** La vuelta va a un programa de este ordenador (Claude Code). */
  loopback: boolean;
  /** Lo que se concederá, ya recortado por el rol. */
  scopes: string[];
  login: string;
  rol: string;
}

export const fetchSolicitudOAuth = (id: string): Promise<SolicitudOAuth> =>
  request<SolicitudOAuth>(`/api/oauth/solicitudes/${encodeURIComponent(id)}`);

export const decidirOAuth = (id: string, aprobar: boolean): Promise<{ redireccion: string }> =>
  request<{ redireccion: string }>(`/api/oauth/solicitudes/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ aprobar }),
  });

export interface ConexionClaude {
  familia: string;
  cliente: string;
  nombre: string | null;
  scopes: string[];
  creada: string;
  caduca: string;
}

export const fetchConexiones = (): Promise<{ conexiones: ConexionClaude[] }> =>
  request<{ conexiones: ConexionClaude[] }>('/api/oauth/conexiones');

export const revocarConexion = (familia: string): Promise<{ ok: true }> =>
  request<{ ok: true }>(`/api/oauth/conexiones/${encodeURIComponent(familia)}`, {
    method: 'DELETE',
  });

/**
 * A dónde volver tras entrar: solo rutas del propio portal. La misma regla que la API
 * (`rutaDeVuelta` en apps/api/src/oauth/reglas.ts): `//host` o `/\host` serían otro dominio.
 */
export function rutaDeVuelta(valor: string | null): string {
  if (!valor || valor.length > 1000) return '/';
  return /^\/(?![/\\])[\x21-\x7e]*$/.test(valor) ? valor : '/';
}
